import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAccount, useConnect, useDisconnect, useChainId, useSwitchChain } from 'wagmi';
import { ethers } from 'ethers';
import logger from '../../utils/logger';

// Define wallet types for compatibility
export type WalletType = 'metamask' | 'coinbase' | null;

interface WagmiWalletContextType {
    // Wagmi-based properties
    address: string | undefined;
    isConnected: boolean;
    isConnecting: boolean;
    chainId: number;

    // Legacy compatibility properties (for existing components)
    provider: ethers.providers.Web3Provider | null;
    signer: ethers.Signer | null;
    chainIdString: string; // Legacy string format
    isInitialized: boolean;
    initializationError: string | null;
    executionStatus: string;
    activeWalletType: WalletType;

    // Methods
    connectWallet: (walletType?: WalletType) => Promise<{ chainId: string } | null>;
    setExecutionStatus: (status: string) => void;
    initializeProvider: (walletType?: WalletType) => Promise<{ chainId: string } | null>;
    updateChainId: () => Promise<void>;
    switchWalletType: (walletType: WalletType) => Promise<boolean>;
    switchChain: (chainId: number) => Promise<void>;
}

const WagmiWalletContext = createContext<WagmiWalletContextType | undefined>(undefined);

export const WagmiWalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Wagmi hooks
    const { address, isConnected, isConnecting, connector } = useAccount();
    const { connect, connectors } = useConnect();
    const { disconnect } = useDisconnect();
    const chainId = useChainId();
    const { switchChain } = useSwitchChain();

    // Legacy state for compatibility
    const [provider, setProvider] = useState<ethers.providers.Web3Provider | null>(null);
    const [signer, setSigner] = useState<ethers.Signer | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [initializationError, setInitializationError] = useState<string | null>(null);
    const [executionStatus, setExecutionStatus] = useState('');
    const [activeWalletType, setActiveWalletType] = useState<WalletType>(null);

    // Track previous connection state to detect disconnects
    const [wasConnected, setWasConnected] = useState(false);

    // CRITICAL FIX: Detect wallet disconnects and prevent Auth0 logout + backup state
    useEffect(() => {
        // If we were connected and now we're not, it's a disconnect event
        if (wasConnected && !isConnected) {
            logger.log('🔌 Wallet disconnect detected - setting protection flag and backing up state');

            // Use localStorage (survives Coinbase's aggressive sessionStorage clearing)
            localStorage.setItem('wallet_disconnect_in_progress', Date.now().toString());

            // 🔄 CRITICAL: Backup tab state to localStorage for Coinbase page reload recovery
            try {
                const currentTabState = sessionStorage.getItem('tabState');
                if (currentTabState) {
                    localStorage.setItem('tabState_backup', currentTabState);
                    logger.log('💾 BACKUP: Tab state backed up to localStorage for Coinbase reload recovery');
                }
            } catch (error) {
                logger.error('Error backing up tab state:', error);
            }

            // Auto-clear after 3 seconds (grace period for wallet switching)
            setTimeout(() => {
                localStorage.removeItem('wallet_disconnect_in_progress');
                logger.log('🔌 Wallet disconnect protection flag cleared');
            }, 3000);
        }

        // Update tracking state
        setWasConnected(isConnected);
    }, [isConnected, wasConnected]);

    // Update provider and signer when account changes
    useEffect(() => {
        const updateProvider = async () => {
            if (isConnected && address && window.ethereum) {
                try {
                    // Create provider with ENS disabled to prevent ENS resolution errors
                    const web3Provider = new ethers.providers.Web3Provider(
                        window.ethereum as any,
                        {
                            name: 'custom',
                            chainId: chainId,
                            ensAddress: undefined // Disable ENS resolution
                        }
                    );
                    setProvider(web3Provider);
                    setSigner(web3Provider.getSigner());
                    setIsInitialized(true);
                    setInitializationError(null);

                    // Determine wallet type from connector
                    if (connector) {
                        if (connector.name.toLowerCase().includes('metamask')) {
                            setActiveWalletType('metamask');
                        } else if (connector.name.toLowerCase().includes('coinbase')) {
                            setActiveWalletType('coinbase');
                        } else {
                            setActiveWalletType('metamask'); // Default fallback
                        }
                    }

                    logger.log('Provider and signer updated for address:', address);
                } catch (error) {
                    logger.error('Error updating provider:', error);
                    setInitializationError(error instanceof Error ? error.message : 'Unknown error');
                }
            } else {
                setProvider(null);
                setSigner(null);
                setActiveWalletType(null);
                if (!isConnecting) {
                    setIsInitialized(true);
                }
            }
        };

        updateProvider();
    }, [isConnected, address, connector, isConnecting]);

    // Legacy compatibility methods
    const connectWallet = async (walletType?: WalletType): Promise<{ chainId: string } | null> => {
        try {
            setExecutionStatus('Connecting wallet...');

            // Find the appropriate connector
            let targetConnector = connectors[0]; // Default to first connector

            if (walletType === 'metamask') {
                targetConnector = connectors.find(c => c.name.toLowerCase().includes('metamask')) || connectors[0];
            } else if (walletType === 'coinbase') {
                targetConnector = connectors.find(c => c.name.toLowerCase().includes('coinbase')) || connectors[1];
            }

            await connect({ connector: targetConnector });
            setExecutionStatus('Wallet connected successfully');

            return { chainId: chainId.toString() };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Connection failed';
            setExecutionStatus(errorMessage);
            setInitializationError(errorMessage);
            logger.error('Wallet connection error:', error);
            return null;
        }
    };

    const initializeProvider = async (walletType?: WalletType): Promise<{ chainId: string } | null> => {
        return connectWallet(walletType);
    };

    const updateChainId = async (): Promise<void> => {
        // Chain ID is automatically updated by Wagmi
        logger.log('Chain ID updated to:', chainId);
    };

    const switchWalletType = async (walletType: WalletType): Promise<boolean> => {
        try {
            // 🔒 CRITICAL: Mark that we're switching wallets, not logging out
            sessionStorage.setItem('wallet_switching', 'true');
            logger.log('🔄 Wallet switch initiated - Auth0 logout will be prevented');

            if (isConnected) {
                disconnect();
            }

            const result = await connectWallet(walletType);

            // Clear the flag after successful connection
            sessionStorage.removeItem('wallet_switching');
            logger.log('✅ Wallet switch completed');

            return !!result;
        } catch (error) {
            // Clear flag even on error
            sessionStorage.removeItem('wallet_switching');
            logger.error('Error switching wallet type:', error);
            return false;
        }
    };

    const handleSwitchChain = async (targetChainId: number): Promise<void> => {
        try {
            await switchChain({ chainId: targetChainId });
            setExecutionStatus(`Switched to chain ${targetChainId}`);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Chain switch failed';
            setExecutionStatus(errorMessage);
            logger.error('Chain switch error:', error);
            throw error;
        }
    };

    const contextValue: WagmiWalletContextType = {
        // Wagmi properties
        address,
        isConnected,
        isConnecting,
        chainId,

        // Legacy compatibility properties
        provider,
        signer,
        chainIdString: chainId.toString(), // Convert to string for legacy compatibility
        isInitialized,
        initializationError,
        executionStatus,
        activeWalletType,

        // Methods
        connectWallet,
        setExecutionStatus,
        initializeProvider,
        updateChainId,
        switchWalletType,
        switchChain: handleSwitchChain,
    };

    // Expose wallet context to window for direct access (legacy compatibility)
    useEffect(() => {
        (window as any).walletContext = contextValue;
        return () => {
            delete (window as any).walletContext;
        };
    }, [contextValue]);

    return (
        <WagmiWalletContext.Provider value={contextValue}>
            {children}
        </WagmiWalletContext.Provider>
    );
};

export const useWagmiWallet = () => {
    const context = useContext(WagmiWalletContext);
    if (context === undefined) {
        throw new Error('useWagmiWallet must be used within a WagmiWalletProvider');
    }
    return context;
};

// Legacy hook for backward compatibility
export const useWallet = useWagmiWallet;
